/**
 * Canonical date-formatting helpers.
 *
 * Before this module, ~10 features each defined their own `formatLongDate` /
 * `formatShortDate` / `formatDate` copy with subtly different signatures and
 * NaN handling. These are the one true set — every one accepts `Date | string`
 * and returns a graceful placeholder (`''`) for unparseable input.
 *
 * No date library: each helper is a one-liner over `Intl` / `toISOString`.
 */

/**
 * Coerce a `Date | string` to a `Date`, or return `null` when the input is
 * empty / unparseable. The single chokepoint every formatter funnels through.
 */
function toDate(d: Date | string | null | undefined): Date | null {
  if (d == null || d === '') return null;
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * ISO calendar day — `YYYY-MM-DD`. Mirrors the repeated
 * `new Date(x).toISOString().slice(0, 10)` idiom. Returns `''` for invalid
 * input.
 */
export function isoDay(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date ? date.toISOString().slice(0, 10) : '';
}

/**
 * en-US long date — e.g. `January 5, 2026`. Returns `''` for invalid input.
 */
export function formatLong(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
}

/**
 * en-US short date — e.g. `Jan 5, 2026`. Returns `''` for invalid input.
 */
export function formatShort(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';
}

/**
 * en-US month + day, no year — e.g. `Jan 5`. Used by the nowcast daily-delta
 * pills and the admin login-history axis. Returns `''` for invalid input.
 */
export function formatMonthDay(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
}

/**
 * en-US month + year, long month — e.g. `January 2026`. Returns `''` for
 * invalid input.
 */
export function formatMonthYear(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    : '';
}

/**
 * en-US month + year, abbreviated month — e.g. `Jan 2026`. Used by the
 * vintage-slider range markers. Returns `''` for invalid input.
 */
export function formatShortMonthYear(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    : '';
}

/**
 * en-US date + time — e.g. `Jan 5, 2026, 02:30 PM`. Matches the admin pages'
 * previous `formatDateTime`. Returns `'-'` for invalid input (the placeholder
 * the admin copy used).
 */
export function formatDateTime(d: Date | string | null | undefined): string {
  const date = toDate(d);
  return date
    ? date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
}
